<!--
  Permite elegir una paleta del catálogo, la apariencia predeterminada o un color
  personalizado. Los dos grupos van en pestañas para no recorrer las treinta a la vez.
  «Por defecto» abre la de Vivos y «Personalizado» la cierra; los Pasteles van solos.

  Cada paleta es un cuadro pintado con su color principal, con «Aa» y los otros tres
  colores en círculos superpuestos. Al apuntarla, «Aa» deja su sitio al nombre y los
  círculos se reparten a lo ancho. El cuadro lleva su propio data-palette, así que los colores salen
  de palettes.css y no se repiten aquí.

  «Personalizado» abre un popup con el ColorPicker, anclado al propio cuadro.
-->
<script lang="ts">
  import { DEFAULT_BRAND_COLOR } from "@shared/brand";
  import type { PaletteGroup } from "@shared/palettes";
  import { CUSTOM_PALETTE, PALETTE_GROUPS, PALETTES } from "@shared/palettes";
  import type { AppTheme } from "@shared/types";

  import { cx } from "../lib/cx";
  import Icon from "./Icon.svelte";
  import ColorPicker from "./ui/ColorPicker.svelte";

  let { value, onChange }: { value: AppTheme; onChange: (next: AppTheme) => void } = $props();

  const uid = $props.id();
  const popoverId = `popover-color-${uid}`;
  const anchorName = `--palette-custom-${uid}`;

  const groupOf = (palette: string | null): PaletteGroup =>
    PALETTES.find((p) => p.id === palette)?.group ?? "vivid";

  // Se entra al grupo de la paleta puesta -- también si `value` llega
  // después del montaje, como al cargar los ajustes de una aplicación --;
  // desde ahí cada quien cambia de pestaña a su gusto (el derived se deja
  // sobreescribir) y solo se recalcula si `value.palette` cambia de verdad.
  let activeTab = $derived<PaletteGroup>(groupOf(value.palette));
  const shown = $derived(PALETTES.filter((p) => p.group === activeTab));

  const customColor = $derived(value.color ?? DEFAULT_BRAND_COLOR);
  const isCustom = $derived(value.palette === CUSTOM_PALETTE);

  // Elegir una paleta no se lleva el tamaño de letra: son dos mandos.
  const pick = (palette: string | null) => onChange({ ...value, palette });
  const pickColor = (color: string) => onChange({ ...value, palette: CUSTOM_PALETTE, color });

  let popover = $state<HTMLDivElement | null>(null);

  /* Con el popup abierto, Escape lo cierra a él y no sigue hasta el modal
     de alrededor (CreateAppModal escucha la tecla en `window`). */
  function onKey(e: KeyboardEvent) {
    if (e.key !== "Escape" || !popover?.matches(":popover-open")) return;
    e.stopPropagation();
    popover.hidePopover();
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="picker-palette" onkeydown={onKey}>
  <div class="chips" role="tablist" aria-label="Grupos de paletas">
    {#each PALETTE_GROUPS as group (group.id)}
      <button
        type="button"
        role="tab"
        aria-selected={activeTab === group.id}
        class={cx("btn-palette-tab", "chip", activeTab === group.id && "active")}
        onclick={() => {
          activeTab = group.id;
          // El popup va anclado a «Personalizado», que en Pasteles no está.
          popover?.hidePopover();
        }}
      >
        {group.label}
      </button>
    {/each}
  </div>

  <div class="grid-palette">
    {#if activeTab === "vivid"}
      {@render tile(null, "Por defecto", "none")}
    {/if}
    {#each shown as palette (palette.id)}
      {@render tile(palette.id, palette.name, palette.id)}
    {/each}

    {#if activeTab === "vivid"}
      <button
        type="button"
        class={cx("btn-palette-custom", "tile-palette", isCustom && "selected")}
        data-palette="custom"
        style:--palette-1={customColor}
        style:anchor-name={anchorName}
        aria-pressed={isCustom}
        aria-label="Personalizado"
        popovertarget={popoverId}
        onclick={() => {
          if (!isCustom) pickColor(customColor);
        }}
      >
        <span class="face-palette">
          <span class="lead-palette"><Icon name="color-picker" size={22} /></span>
          <span class="name-palette">Personalizado</span>
        </span>
        <span class="dots-palette" aria-hidden="true">
          <i></i>
          <i></i>
          <i></i>
        </span>
      </button>
    {/if}
  </div>

  <div
    id={popoverId}
    class="popover-color-custom"
    popover="auto"
    bind:this={popover}
    style:position-anchor={anchorName}
  >
    <ColorPicker value={customColor} onChange={pickColor} />
  </div>
</div>

{#snippet tile(id: string | null, name: string, attr: string)}
  <button
    type="button"
    class={cx("btn-pick-palette", "tile-palette", value.palette === id && "selected")}
    data-palette={attr}
    aria-pressed={value.palette === id}
    aria-label={name}
    onclick={() => pick(id)}
  >
    <span class="face-palette">
      <span class="lead-palette">Aa</span>
      <span class="name-palette">{name}</span>
    </span>
    <span class="dots-palette" aria-hidden="true">
      <i></i>
      <i></i>
      <i></i>
    </span>
  </button>
{/snippet}

<style>
  /*
   * Una medida para todo: el cuadro, y dentro de él un solo margen
   * (`--_inset`) para los círculos y para el texto. El hueco de la rejilla es
   * ese mismo margen, y el del popup también.
   */
  .picker-palette {
    --_tile: 4.75rem;
    --_inset: var(--sp-8);
    --_dot: 1.375rem;
    /* El acento del panel, resuelto aquí: dentro de cada cuadro `--accent`
       ya es el de su propia paleta. */
    --_ring: var(--accent);

    display: flex;
    flex-direction: column;
    gap: var(--sp-12);
  }

  .grid-palette {
    display: grid;
    grid-template-columns: repeat(auto-fill, var(--_tile));
    gap: var(--_inset);
  }

  .tile-palette {
    /* La tinta sobre el color principal: oscura si es claro, blanca si es
       oscuro, igual que hace palettes.css con `--accent-text`. */
    --_ink: oklch(from var(--palette-1) clamp(0.2, (l - 0.72) * -1000, 0.98) 0.02 h);

    position: relative;
    width: var(--_tile);
    height: var(--_tile);
    padding: 0;
    border: 0;
    border-radius: var(--radius-lg);
    background: var(--palette-1);
    color: var(--_ink);
    cursor: pointer;
    overflow: hidden;
    transition:
      transform 0.18s,
      box-shadow 0.15s;

    &:hover {
      transform: translateY(-0.125rem);
    }

    /* La elegida lleva un anillo del acento del panel (`--_ring`), no del
       de su paleta. */
    &.selected,
    &:focus-visible {
      outline: none;
      box-shadow:
        0 0 0 2px var(--bg-level2),
        0 0 0 4px var(--_ring);
    }
  }

  /* El texto se centra en lo que queda por encima de los círculos. */
  .face-palette {
    position: absolute;
    inset: 0 0 calc(var(--_dot) + var(--_inset)) 0;
    display: grid;
    place-items: center;

    & > span {
      grid-area: 1 / 1;
      transition:
        opacity 0.2s,
        transform 0.2s;
    }
  }

  .lead-palette {
    display: inline-flex;
    font-size: 1.375rem;
    font-weight: 700;
    line-height: 1;
  }

  .name-palette {
    max-width: calc(var(--_tile) - var(--_inset));
    overflow: hidden;
    font-size: var(--text-xs);
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
    opacity: 0;
    transform: translateY(0.25rem);
  }

  .tile-palette:is(:hover, :focus-visible) {
    & .lead-palette {
      opacity: 0;
      transform: translateY(-0.25rem);
    }

    & .name-palette {
      opacity: 1;
      transform: none;
    }

    /* De borde a borde del cuadro: lo que sobra (o falta) entre los tres
       se reparte en los dos huecos. */
    & .dots-palette i + i {
      margin-left: calc((var(--_tile) - var(--_inset) * 2 - var(--_dot) * 3) / 2);
    }
  }

  /* Los tres de apoyo: superpuestos a la mitad, el primero encima. */
  .dots-palette {
    position: absolute;
    left: var(--_inset);
    bottom: var(--_inset);
    display: flex;

    & i {
      --_c: var(--palette-2);

      display: block;
      width: var(--_dot);
      height: var(--_dot);
      border-radius: var(--radius-full);
      background: var(--_c);
      /* Una sombra suave de su mismo color, más oscuro: entre pasteles
         parecidos, o sobre un principal del mismo tono, es lo que separa un
         círculo de otro. Un borde fino se veía pixelado en la curva. Dentro,
         un pelo de su tinte más claro lo realza; a medias de opacidad para que
         tampoco marque un filo. */
      box-shadow:
        inset 0 0 0 1px oklch(from var(--_c) calc(l + 0.1) c h / 0.6),
        0 0 0.1875rem oklch(from var(--_c) calc(l - 0.25) c h / 0.5);
      transition: margin 0.22s cubic-bezier(0.3, 1.4, 0.5, 1);

      & + i {
        margin-left: calc(var(--_dot) / -2);
      }

      &:nth-child(1) {
        z-index: 3;
      }
      &:nth-child(2) {
        --_c: var(--palette-3);

        z-index: 2;
      }
      &:nth-child(3) {
        --_c: var(--palette-4);
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .tile-palette,
    .face-palette > span,
    .dots-palette i {
      transition: none;
    }
  }

  /* El popup sale bajo «Personalizado», alineado con su borde izquierdo, y
     si abajo no cabe se va arriba. */
  .popover-color-custom {
    position-area: bottom span-right;
    position-try-fallbacks: flip-block, flip-inline;
    margin: var(--sp-8) 0;
    /* El mismo hueco que separa los bloques del ColorPicker. */
    padding: var(--sp-12);
    border: var(--border-width) solid var(--edge-strong);
    border-radius: var(--radius-xl);
    background: var(--bg-float);
    box-shadow: var(--sheen), var(--shadow-xl);
    transform-origin: top left;
    opacity: 0;
    scale: var(--mo-shrink);
    transition:
      opacity var(--mo-t-menu) var(--mo-move),
      scale var(--mo-t-menu) var(--mo-spring),
      overlay var(--mo-t-menu) allow-discrete,
      display var(--mo-t-menu) allow-discrete;

    &:popover-open {
      opacity: 1;
      scale: 1;

      @starting-style {
        opacity: 0;
        scale: var(--mo-shrink);
      }
    }
  }
</style>
